import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useSectionNav, scrollToTop } from '../lib/atlas';

export function AtlasMark({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="mark-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#713BFF" />
          <stop offset="0.5" stopColor="#C13DDB" />
          <stop offset="1" stopColor="#FF7138" />
        </linearGradient>
      </defs>
      <circle cx="30" cy="34" r="13" fill="url(#mark-g)" />
      <ellipse
        cx="30"
        cy="34"
        rx="26"
        ry="9.5"
        fill="none"
        stroke="url(#mark-g)"
        strokeWidth="2.5"
        transform="rotate(-22 30 34)"
      />
      <circle cx="52" cy="17" r="3.2" fill="#FF7138" />
    </svg>
  );
}

const LINKS = [
  { label: 'Home', id: 'top', testid: 'nav-link-home' },
  { label: 'Explore', id: 'explore', testid: 'nav-link-explore' },
  { label: 'All Work', route: '/work', testid: 'nav-link-work' },
  { label: 'About', id: 'about', testid: 'nav-link-about' },
  { label: 'Contact', id: 'contact', testid: 'nav-link-contact' },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const goTo = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const handleLink = (link) => {
    if (link.route) {
      navigate(link.route);
    } else if (link.id === 'top') {
      if (location.pathname !== '/') navigate('/');
      else scrollToTop();
    } else {
      goTo(link.id);
    }
  };

  return (
    <>
      <header
        data-testid="nav-main"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-void/80 backdrop-blur-xl border-b border-white/[0.07]'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-16 py-4">
          <button
            data-testid="nav-wordmark"
            onClick={() => {
              if (location.pathname !== '/') navigate('/');
              else scrollToTop();
            }}
            className="flex items-center gap-2.5 group"
          >
            <AtlasMark className="w-6 h-6" />
            <span className="font-syne font-bold tracking-[0.18em] text-[13px] text-white group-hover:text-violettext transition-colors">
              THE CREATIVE ATLAS
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((link) => (
              <button
                key={link.label}
                data-testid={link.testid}
                onClick={() => handleLink(link)}
                className={`font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  link.route && location.pathname === link.route
                    ? 'text-tangerine'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <button
            data-testid="nav-menu-button"
            className="md:hidden text-white p-1"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-void/95 backdrop-blur-2xl md:hidden flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-7">
              {LINKS.map((link, i) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  onClick={() => handleLink(link)}
                  className="text-left font-syne font-bold text-4xl text-white tracking-tight"
                >
                  {link.label}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
